import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle, ArrowRight, Sparkles } from "lucide-react";
import { useTranslation } from "react-i18next";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";

/**
 * Return-from-Stripe landing page for Shop purchases.
 *
 * Polls `GET /api/shop/status/{session_id}` a few times so we don't rely on
 * the webhook to fulfil before the user sees confirmation. Stripe hosted
 * checkout redirects here with `?session_id={CHECKOUT_SESSION_ID}` — the
 * placeholder is replaced by Stripe before the redirect.
 *
 * Terminal states:
 *   paid    → success card with a link back to the Shop
 *   expired → "session expired" card
 *   unpaid  → same as expired but a slightly softer message
 * Non-terminal polling times out after ~15 s and shows a "still working"
 * card that reassures the user the webhook will finish it silently.
 */
const POLL_INTERVAL_MS = 1500;
const MAX_ATTEMPTS = 10;

export default function ShopSuccess() {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("polling"); // polling | paid | expired | error | timeout
  const [itemId, setItemId] = useState(null);
  const attemptsRef = useRef(0);

  useEffect(() => {
    if (!sessionId) {
      setStatus("error");
      return;
    }
    let cancelled = false;

    const poll = async () => {
      attemptsRef.current += 1;
      try {
        const { data } = await api.get(`/shop/status/${sessionId}`);
        if (cancelled) return;
        setItemId(data.item_id || null);
        if (data.payment_status === "paid" || data.status === "paid") {
          setStatus("paid");
          return;
        }
        if (data.status === "expired") {
          setStatus("expired");
          return;
        }
        if (attemptsRef.current >= MAX_ATTEMPTS) {
          setStatus("timeout");
          return;
        }
        setTimeout(poll, POLL_INTERVAL_MS);
      } catch {
        if (cancelled) return;
        setStatus("error");
      }
    };

    poll();
    return () => { cancelled = true; };
  }, [sessionId]);

  return (
    <div data-testid="shop-success-root" className="min-h-[60vh] flex items-center justify-center px-6 py-16">
      <div className="max-w-lg w-full bg-white border border-slate-200 rounded-2xl p-10 text-center">
        {status === "polling" && (
          <>
            <Loader2 size={40} className="mx-auto animate-spin text-blue-800" />
            <h1 className="mt-6 font-display font-bold text-2xl text-slate-900">{t("shopSuccessUi.confirmingOrder")}</h1>
            <p className="mt-3 text-sm text-slate-600 leading-relaxed">
              {t("shopSuccessUi.stripeFinalizing")}
            </p>
          </>
        )}

        {status === "paid" && (
          <>
            <div className="mx-auto w-16 h-16 rounded-full bg-emerald-50 flex items-center justify-center">
              <CheckCircle2 size={32} className="text-emerald-600" />
            </div>
            <div className="mt-4 inline-flex items-center gap-1.5 rounded-full bg-emerald-50 border border-emerald-200 px-3 py-1 text-[10px] uppercase tracking-[0.28em] text-emerald-700 font-bold">
              <Sparkles size={11} /> {t("shopSuccessUi.paymentReceived")}
            </div>
            <h1 className="mt-4 font-display font-bold text-3xl text-slate-900 tracking-tight">
              {t("shopSuccessUi.youreIn")}
            </h1>
            <p className="mt-3 text-sm text-slate-600 leading-relaxed">
              {t("shopSuccessUi.orderConfirmed")}
              {itemId === "mentor-call" && (
                <> {t("shopSuccessUi.mentorCallDetails")}</>
              )}
              {itemId === "textbook" && (
                <> {t("shopSuccessUi.textbookShipping")}</>
              )}
              {itemId === "trader-package" && (
                <> {t("shopSuccessUi.traderPackageShipping")}</>
              )}
            </p>
            <div className="mt-8 flex flex-wrap justify-center gap-3">
              <Link to="/shop">
                <Button data-testid="shop-success-back" variant="outline" className="h-11 px-5">
                  {t("shopSuccessUi.backToShop")}
                </Button>
              </Link>
              <Link to="/dashboard">
                <Button data-testid="shop-success-dashboard" className="h-11 px-5 bg-blue-800 hover:bg-blue-700">
                  {t("shopSuccessUi.goToDashboard")} <ArrowRight size={14} className="ml-2" />
                </Button>
              </Link>
            </div>
          </>
        )}

        {(status === "expired" || status === "error") && (
          <>
            <div className="mx-auto w-16 h-16 rounded-full bg-rose-50 flex items-center justify-center">
              <XCircle size={32} className="text-rose-600" />
            </div>
            <h1 className="mt-4 font-display font-bold text-2xl text-slate-900">
              {status === "expired" ? t("shopSuccessUi.checkoutExpired") : t("shopSuccessUi.couldNotConfirm")}
            </h1>
            <p className="mt-3 text-sm text-slate-600 leading-relaxed">
              {t("shopSuccessUi.noChargeMade")}
            </p>
            <Link to="/shop" className="inline-block mt-6">
              <Button className="h-11 px-5 bg-blue-800 hover:bg-blue-700">
                {t("shopSuccessUi.backToShop")} <ArrowRight size={14} className="ml-2" />
              </Button>
            </Link>
          </>
        )}

        {status === "timeout" && (
          <>
            <Loader2 size={40} className="mx-auto animate-spin text-amber-500" />
            <h1 className="mt-6 font-display font-bold text-2xl text-slate-900">{t("shopSuccessUi.stillFinalizing")}</h1>
            <p className="mt-3 text-sm text-slate-600 leading-relaxed">
              {t("shopSuccessUi.takingLonger")}
            </p>
            <Link to="/shop" className="inline-block mt-6">
              <Button variant="outline" className="h-11 px-5">
                {t("shopSuccessUi.backToShop")}
              </Button>
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
